import { useEffect, useState } from "react";
import { ClipboardList, Clock3, LayoutGrid, PersonStanding, Scale, Smile, Syringe } from "lucide-react";
import { useSaveShortcuts, useShortcutsSettings } from "../../../queries/usePluginSettingsQuery";
import "./ShortcutsPlugin.css";

export const SHORTCUTS_PLUGIN_ID = "shortcuts";

// Shortcuts chosen before they were kept on the server: only read to recover that choice.
export const LEGACY_SHORTCUTS_STORAGE_KEY = "doctolik-plugin-shortcuts-settings";

/** Icon and colour of each plugin (same as Configuration > Plugins). */
export const PLUGIN_LOOK = {
	timeline: { icon: Clock3, color: "#e2678f" },
	questionnaire: { icon: ClipboardList, color: "#e8a63a" },
	atlas: { icon: PersonStanding, color: "#2f9e8f" },
	odontogram: { icon: Smile, color: "#3b7bff" },
	bmi: { icon: Scale, color: "#e50158" },
	vaccination: { icon: Syringe, color: "#0f9b7a" },
	shortcuts: { icon: LayoutGrid, color: "#475569" },
};

export const pluginLook = (id) => PLUGIN_LOOK[id] ?? { icon: LayoutGrid, color: "#64748b" };

export function readLegacyShortcuts() {
	try {
		const saved = JSON.parse(localStorage.getItem(LEGACY_SHORTCUTS_STORAGE_KEY));
		return Array.isArray(saved?.pluginIds) ? saved.pluginIds.filter((id) => typeof id === "string" && id !== SHORTCUTS_PLUGIN_ID) : [];
	} catch {
		return [];
	}
}

/**
 * The doctor's shortcuts, from the server: `{ pluginIds, isPending, isError }`. Until the doctor
 * saves them on the server, a choice left in this browser by the previous version is used.
 */
export function useShortcuts() {
	const query = useShortcutsSettings();
	const legacy = query.isSuccess && !query.data.saved ? readLegacyShortcuts() : [];
	const pluginIds = query.data?.saved ? query.data.pluginIds : legacy;
	return {
		pluginIds: query.isSuccess ? pluginIds : [],
		// Recovered from this browser, not saved on the server yet.
		fromLegacy: legacy.length > 0,
		isPending: query.isPending,
		isError: query.isError,
		refetch: query.refetch,
	};
}

/**
 * Configuration of the Shortcuts plugin: one switch per existing plugin, then "Save" (kept on the
 * server for the doctor). The plugins switched on are launched directly from the Shortcuts plugin
 * of the consultation. `plugins`: `{ id, name, description }[]`, the doctor's plugins.
 */
export function ShortcutsPluginSettings({ plugins, onClose }) {
	const shortcuts = useShortcuts();
	const save = useSaveShortcuts();
	const [draft, setDraft] = useState(null);
	const [message, setMessage] = useState(null);
	const choices = plugins.filter((plugin) => plugin.id !== SHORTCUTS_PLUGIN_ID);
	const saved = shortcuts.pluginIds;
	const current = draft ?? saved;
	const chosen = current.filter((id) => choices.some((plugin) => plugin.id === id));
	const changed = shortcuts.fromLegacy || (draft !== null && (draft.length !== saved.length || draft.some((id, index) => saved[index] !== id)));
	const savedKey = saved.join(",");

	// A new choice starts from the saved one, once loaded.
	useEffect(() => {
		setDraft(null);
	}, [savedKey]);

	const toggle = (id) => {
		setMessage(null);
		setDraft(current.includes(id) ? current.filter((pluginId) => pluginId !== id) : [...current, id]);
	};

	const submit = async () => {
		setMessage(null);
		try {
			const result = await save.mutateAsync(chosen);
			setMessage({ ok: true, text: `Shortcuts saved (${result.pluginIds.length}).` });
		} catch (error) {
			setMessage({ ok: false, text: `Shortcuts not saved: ${error?.message ?? error}` });
		}
	};

	return <>
		<div className="PluginModalBody ShortcutsSettings">
			<p className="ShortcutsSettingsHint">Switch on the plugins to launch directly from the Shortcuts plugin of the consultation, then save. Your choice is kept for your account, on every computer. The other plugins stay displayed as usual.</p>
			{shortcuts.isPending ? (
				<p className="ShortcutsSettingsHint" role="status">Loading your shortcuts…</p>
			) : shortcuts.isError ? (
				<p className="ShortcutsSettingsError" role="alert">Your shortcuts could not be loaded. <button type="button" className="Btn Btn--ghost" onClick={() => shortcuts.refetch()}>Retry</button></p>
			) : (
				<>
					<p className="ShortcutsSettingsCount">{chosen.length} shortcut{chosen.length === 1 ? "" : "s"} selected</p>
					<ul className="ShortcutsSettingsList">
						{choices.map((plugin) => {
							const { icon: Icon, color } = pluginLook(plugin.id);
							const on = current.includes(plugin.id);
							return (
								<li key={plugin.id}>
									<span className="ShortcutsSettingsIcon" style={{ backgroundColor: color }}><Icon size={18} aria-hidden="true" /></span>
									<span className="ShortcutsSettingsName"><strong>{plugin.name}</strong>{plugin.description && <small>{plugin.description}</small>}</span>
									<button
										type="button"
										role="switch"
										aria-checked={on}
										aria-label={`Shortcut ${plugin.name}`}
										className={`ShortcutsSwitch${on ? " is-on" : ""}`}
										onClick={() => toggle(plugin.id)}
									>
										<span aria-hidden="true" />
									</button>
								</li>
							);
						})}
					</ul>
				</>
			)}
			{message && <p className={message.ok ? "ShortcutsSettingsSaved" : "ShortcutsSettingsError"} role={message.ok ? "status" : "alert"}>{message.text}</p>}
		</div>
		<div className="PluginModalFooter">
			<button className="Btn Btn--ghost" type="button" onClick={onClose}>{changed ? "Cancel" : "Close"}</button>
			<button className="Btn" type="button" disabled={!changed || save.isPending || shortcuts.isPending} onClick={submit}>{save.isPending ? "Saving…" : "Save"}</button>
		</div>
	</>;
}
